import { useState, useEffect } from 'react'
import { useDebounce } from '@/client/hooks/useDebounce'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ArrowLeft, XCircle, Search, AlertTriangle, AlertCircle, Info, Download, FileSpreadsheet, FileJson, Loader2, TableProperties, BarChart3, FileText, Globe, Link2, Shield, Lock, FileWarning, GitCompare, Map, RotateCw, FolderTree, BookOpen, Pickaxe, ArrowUpDown, ImageIcon, Clock, Pause, Play, Zap, ChevronLeft, ChevronRight, ChevronDown, Sparkles, MoreHorizontal } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTab } from '../components/DataTab'
import { OverviewTab } from '../components/OverviewTab'
import { PageDetailSheet } from '../components/PageDetailSheet'
import { ExternalLinksTab } from '../components/ExternalLinksTab'
import { FilteredPagesTab } from '../components/FilteredPagesTab'
import { CompareTab } from '../components/CompareTab'
import { LinksTab } from '../components/LinksTab'
import { SitemapTab } from '../components/SitemapTab'
import { StructureTab } from '../components/StructureTab'
import { ContentIntelligenceTab } from '../components/ContentIntelligenceTab'
import { SecurityTab } from '../components/SecurityTab'
import { ExtractionsTab } from '../components/ExtractionsTab'
import { ImagesTab } from '../components/ImagesTab'
import { ShareReportDialog } from '../components/ShareReportDialog'
import { issueDescriptions, ISSUE_CATEGORIES, getCategoryLabels, computePriority, getIssueCategory } from '../data/issueDescriptions'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toast } from 'sonner'
import { decodeHtmlEntities } from '@/client/lib/decode'
import {
  useCrawl,
  useCrawlIssues,
  useCrawlPages,
  useIssuePages,
  useCancelCrawl,
  usePauseCrawl,
  useResumeCrawl,
  useExportCrawl,
  useWordPressInfo,
  useRunLighthouse,
  useLighthouseData,
  type CrawlIssue,
  type AiIssuePage,
  type ExportType,
  type ExportFormat,
} from '../hooks/useCrawls'

function statusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    pending: 'secondary',
    running: 'default',
    paused: 'secondary',
    completed: 'outline',
    failed: 'destructive',
    cancelled: 'secondary',
  }
  return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>
}

function severityIcon(severity: string) {
  switch (severity) {
    case 'error':
      return <AlertCircle className="h-4 w-4 text-destructive" />
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />
    default:
      return <Info className="h-4 w-4 text-blue-500" />
  }
}

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatEta(startedAt: string, pagesCrawled: number, pagesFound: number): string {
  const elapsedMs = Date.now() - new Date(startedAt).getTime()
  if (elapsedMs <= 0 || pagesCrawled <= 0) return ''
  const rate = pagesCrawled / (elapsedMs / 1000) // pages per second
  const remaining = pagesFound - pagesCrawled
  const etaSeconds = remaining / rate
  if (!isFinite(etaSeconds) || etaSeconds < 0) return ''
  if (etaSeconds < 60) return `~${Math.round(etaSeconds)}s remaining`
  if (etaSeconds < 3600) return `~${Math.round(etaSeconds / 60)}m remaining`
  const hours = Math.floor(etaSeconds / 3600)
  const minutes = Math.round((etaSeconds % 3600) / 60)
  return `~${hours}h ${minutes}m remaining`
}

function ProgressCard({ crawl }: { crawl: any }) {
  // Parse config for display badges
  let crawlMode: 'spider' | 'list' = 'spider'
  let listUrlCount = 0
  let robotsBypassed = false
  let checkExternalLinks = false
  let maxPages: number | undefined
  try {
    const cfg = JSON.parse(crawl.config)
    crawlMode = cfg.mode || 'spider'
    listUrlCount = Array.isArray(cfg.urls) ? cfg.urls.length : 0
    robotsBypassed = cfg.respectRobots === false
    checkExternalLinks = cfg.checkExternalLinks === true
    maxPages = typeof cfg.maxPages === 'number' ? cfg.maxPages : undefined
  } catch { /* use defaults */ }

  // Effective target = min(pages_found, max_pages) so progress/ETA reflect what'll actually be crawled
  const targetPages = maxPages
    ? Math.min(crawl.pages_found || 0, maxPages)
    : crawl.pages_found || 0
  const progress = targetPages > 0
    ? Math.min(100, Math.round((crawl.pages_crawled / targetPages) * 100))
    : 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <CardTitle className="text-lg truncate">{crawl.domain}</CardTitle>
            {crawlMode === 'list' && (
              <Badge variant="outline" className="shrink-0">List ({listUrlCount} URLs)</Badge>
            )}
            {robotsBypassed && (
              <Badge variant="outline" className="shrink-0 border-yellow-500 text-yellow-600 dark:text-yellow-400">Robots bypassed</Badge>
            )}
            {checkExternalLinks && (
              <Badge variant="outline" className="shrink-0">External checks</Badge>
            )}
          </div>
          {statusBadge(crawl.status)}
        </div>
        <CardDescription className="truncate">{crawl.url}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {(crawl.status === 'running' || crawl.status === 'paused') && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                {crawl.status === 'paused'
                  ? 'Progress (Paused)'
                  : progress >= 100
                    ? 'Finalising — screenshots, JS rendering, analysis'
                    : 'Progress'}
              </span>
              <div className="flex items-center gap-3 text-right">
                <span>{crawl.pages_crawled} / {targetPages} pages ({progress}%)</span>
                {crawl.status === 'running' && crawl.pages_crawled >= 3 && crawl.pages_crawled < targetPages && crawl.started_at && (
                  <span className="text-xs text-muted-foreground">{formatEta(crawl.started_at, crawl.pages_crawled, targetPages)}</span>
                )}
              </div>
            </div>
            <Progress value={progress} />
          </div>
        )}

        {crawl.status === 'completed' && crawl.pages_crawled === 0 && !robotsBypassed && (
          <div className="flex items-start gap-3 rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-950/30 p-3 text-sm">
            <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-300">No pages were crawled</p>
              <p className="text-yellow-700 dark:text-yellow-400 mt-0.5">
                This site's robots.txt likely blocks crawling. Try re-crawling with "Respect robots.txt" turned off.
                Staging sites (e.g. .wpdns.site, .rocket.site) typically block all crawlers by default.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <p className="text-muted-foreground">Pages Crawled</p>
            <p className="text-xl font-semibold">{crawl.pages_crawled}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Pages Found</p>
            <p className="text-xl font-semibold">{crawl.pages_found}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Started</p>
            <p className="text-sm">{formatDate(crawl.started_at)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Completed</p>
            <p className="text-sm">{formatDate(crawl.completed_at)}</p>
            {crawl.started_at && crawl.completed_at && (
              <p className="text-xs text-muted-foreground mt-0.5">{formatDuration(crawl.started_at, crawl.completed_at)}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/** Compact duration like "3h 19m" / "47m" / "12s" between two ISO timestamps. */
function formatDuration(startedAt: string, completedAt: string): string {
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0 || Number.isNaN(ms)) return ''
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m`
  return `${seconds}s`
}

// Issue type → column config for the detail table
const ISSUE_COLUMNS: Record<string, Array<{ key: string; label: string; mono?: boolean; truncate?: boolean }>> = {
  // Title issues
  missing_title: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  title_too_long: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }, { key: 'title_length', label: 'Length' }],
  title_too_short: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }, { key: 'title_length', label: 'Length' }],
  duplicate_titles: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }],
  // Description
  missing_description: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  description_too_long: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'meta_description', label: 'Description', truncate: true }, { key: 'meta_description_length', label: 'Length' }],
  duplicate_descriptions: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'meta_description', label: 'Description', truncate: true }],
  // Headings
  missing_h1: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  multiple_h1: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'h1_count', label: 'H1 Count' }, { key: 'h1_texts', label: 'H1 Text', truncate: true }],
  h1_too_long: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'h1_texts', label: 'H1 Text', truncate: true }],
  duplicate_h1: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'h1_texts', label: 'H1 Text', truncate: true }],
  non_sequential_headings: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'h1_count', label: 'H1' }, { key: 'h2_count', label: 'H2' }, { key: 'h3_count', label: 'H3' }, { key: 'h4_count', label: 'H4' }, { key: 'h5_count', label: 'H5' }, { key: 'h6_count', label: 'H6' }],
  // Canonical
  missing_canonical: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  canonical_mismatch: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical URL', mono: true, truncate: true }],
  // Links
  broken_internal_links: [{ key: 'source_url', label: 'Source', mono: true, truncate: true }, { key: 'target_url', label: 'Target', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  broken_external_links: [{ key: 'source_url', label: 'Source', mono: true, truncate: true }, { key: 'target_url', label: 'Target', mono: true, truncate: true }, { key: 'status_code', label: 'Initial' }, { key: 'final_status_code', label: 'Final' }],
  high_outlink_count: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'internal_links_count', label: 'Links' }],
  orphan_pages: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }],
  nofollow_internal_links: [{ key: 'source_url', label: 'Source', mono: true, truncate: true }, { key: 'target_url', label: 'Target', mono: true, truncate: true }, { key: 'anchor_text', label: 'Anchor', truncate: true }, { key: 'rel', label: 'Rel' }],
  empty_anchor_text: [{ key: 'source_url', label: 'Source', mono: true, truncate: true }, { key: 'target_url', label: 'Target', mono: true, truncate: true }, { key: 'link_type', label: 'Type' }, { key: 'occurrences', label: 'Count' }],
  // Status codes
  server_errors: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  client_errors: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  // Media
  images_missing_alt: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'images_count', label: 'Images' }, { key: 'images_missing_alt', label: 'No Alt' }],
  // Structured data
  missing_schema: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_og: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // Indexing
  noindex_pages: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  redirect_chains: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'redirect_url', label: 'Redirect To', mono: true, truncate: true }],
  // Content
  thin_content: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'word_count', label: 'Words' }],
  missing_viewport: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  mixed_content: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'mixed_content_count', label: 'Count' }],
  // Security / Performance
  missing_hsts: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  slow_response: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'response_time_ms', label: 'Time (ms)' }],
  // Structure
  high_crawl_depth: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'depth', label: 'Depth' }],
  // Security headers
  missing_csp: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_x_frame_options: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // Readability
  low_readability: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'readability_score', label: 'Score' }],
  // URL structure
  url_too_long: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'url_length', label: 'Length' }],
  url_has_underscores: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'url_length', label: 'Length' }],
  url_has_uppercase: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'url_length', label: 'Length' }],
  url_has_parameters: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'url_length', label: 'Length' }],
  url_double_slashes: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'url_length', label: 'Length' }],
  // Additional security headers
  missing_x_content_type_options: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // Sitemap
  missing_sitemap: [{ key: 'reason', label: 'Reason' }],
  sitemap_url_not_crawled: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  page_not_in_sitemap: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // Soft 404
  soft_404_detected: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }, { key: 'status_code', label: 'Status' }],
  // Indexability
  non_indexable_pages: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }, { key: 'canonical_status', label: 'Canonical' }],
  // Redirect loops
  redirect_loop: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'redirect_chain', label: 'Redirect Chain', truncate: true }],
  // Near-duplicate content
  near_duplicate_content: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'near_duplicate_group', label: 'Group' }, { key: 'title', label: 'Title', truncate: true }, { key: 'word_count', label: 'Words' }],
  // Phase 7E: Audit expansion
  missing_lang_attribute: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_favicon: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_twitter_card: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  large_page_size: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'html_size', label: 'Size (bytes)' }],
  description_too_short: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'meta_description', label: 'Description', truncate: true }, { key: 'meta_description_length', label: 'Length' }],
  too_many_redirects: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'redirect_chain', label: 'Chain', truncate: true }],
  links_to_redirects: [{ key: 'source_url', label: 'Source', mono: true, truncate: true }, { key: 'target_url', label: 'Target', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }, { key: 'redirect_url', label: 'Redirect To', mono: true, truncate: true }],
  // 8B: Sitemap cross-checks
  sitemap_url_redirects: [{ key: 'url', label: 'Sitemap URL', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }, { key: 'redirect_url', label: 'Redirect To', mono: true, truncate: true }],
  sitemap_url_noindex: [{ key: 'url', label: 'Sitemap URL', mono: true, truncate: true }, { key: 'meta_robots', label: 'Meta Robots' }],
  sitemap_url_wrong_canonical: [{ key: 'url', label: 'Sitemap URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical', mono: true, truncate: true }],
  // 8C: Canonical chain
  canonical_chain: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Points To', mono: true, truncate: true }, { key: 'chain_target', label: 'Then To', mono: true, truncate: true }],
  canonical_to_noindex: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical (noindex)', mono: true, truncate: true }],
  canonical_to_redirect: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  canonical_to_4xx: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical', mono: true, truncate: true }, { key: 'status_code', label: 'Status' }],
  // 8D: Redirect status
  temporary_redirect_should_be_permanent: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'redirect_chain', label: 'Chain', truncate: true }],
  // 8E: Content freshness
  stale_content: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'last_modified', label: 'Last Modified' }],
  missing_last_modified: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // 8F: Pagination
  paginated_noindex: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'meta_robots', label: 'Meta Robots' }],
  paginated_wrong_canonical: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'canonical_url', label: 'Canonical', mono: true, truncate: true }],
  // 9B: Images
  images_not_modern_format: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'legacy_images_count', label: 'Legacy Images' }],
  images_missing_dimensions: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'images_count', label: 'Total' }, { key: 'images_missing_dimensions', label: 'No Dimensions' }],
  images_no_lazy_loading: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'images_count', label: 'Total' }, { key: 'images_no_lazy_loading', label: 'No Lazy' }],
  // 9C: Breadcrumb
  missing_breadcrumb_schema: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'depth', label: 'Depth' }],
  // 9D: Accessibility
  missing_form_labels: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'issue_count', label: 'Missing Labels' }],
  generic_link_text: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'issue_count', label: 'Generic Links' }],
  empty_buttons: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'issue_count', label: 'Empty Buttons' }],
  missing_skip_nav: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_main_landmark: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  // Spelling
  spelling_errors: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'spelling_count', label: 'Errors' }],
  spelling_suggestions: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'spelling_count', label: 'Suggestions' }],
  // 10E: AI audit
  ai_noai_meta: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'meta_robots', label: 'Meta Robots' }],
  // 11C: JS rendering
  js_rendering_likely: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'word_count', label: 'Words' }, { key: 'html_size', label: 'HTML Size' }],
  // CWV
  poor_lcp: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'psi_lcp', label: 'LCP (ms)' }, { key: 'psi_performance', label: 'Perf Score' }],
  high_cls: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'psi_cls', label: 'CLS' }, { key: 'psi_performance', label: 'Perf Score' }],
  high_tbt: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'psi_tbt', label: 'TBT (ms)' }, { key: 'psi_performance', label: 'Perf Score' }],
  // Hreflang
  hreflang_missing_return: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'hreflang_tags', label: 'Hreflang Tags', truncate: true }],
  hreflang_invalid_target: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'hreflang_tags', label: 'Hreflang Tags', truncate: true }],
  og_locale_mismatch: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'html_lang', label: 'HTML lang' }, { key: 'og_locale', label: 'og:locale' }],
  // Internal authority
  low_inrank_pages: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'inrank', label: 'InRank' }, { key: 'word_count', label: 'Words' }],
  // Spammy links
  spammy_outbound_links: [{ key: 'domain', label: 'Domain' }, { key: 'spam_score', label: 'Spam Score' }, { key: 'link_count', label: 'Links' }],
  // Phase 4: Crawler & Detection Enhancements
  title_matches_h1: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'title', label: 'Title', truncate: true }, { key: 'h1_texts', label: 'H1', truncate: true }],
  missing_video_schema: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'video_count', label: 'Videos' }, { key: 'schema_types', label: 'Schema Types', truncate: true }],
  excessive_iframes: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'iframe_count', label: 'Iframes' }],
  excessive_resources: [{ key: 'url', label: 'URL', mono: true, truncate: true }],
  missing_responsive_images: [{ key: 'url', label: 'URL', mono: true, truncate: true }, { key: 'images_count', label: 'Images' }],
}

// Config for issue types that support expandable sub-item rows
const EXPANDABLE_ISSUE_TYPES: Record<string, { dataKey: string; render: (items: any) => React.ReactNode }> = {
  images_not_modern_format: {
    dataKey: 'legacy_images',
    render: (images: string[]) => (
      <div className="flex flex-wrap gap-2 py-2 px-2">
        {images.map((src, i) => (
          <ImagePreview key={i} src={src} />
        ))}
      </div>
    ),
  },
  spelling_errors: {
    dataKey: 'spelling_items',
    render: (items: Array<{ word: string; suggestions: string[] }>) => (
      <div className="py-2 px-2 space-y-1">
        {items.map((item, i) => (
          <div key={i} className="flex gap-3 text-xs">
            <span className="font-mono text-red-400">{item.word}</span>
            <span className="text-muted-foreground">&rarr;</span>
            <span className="text-green-400">{item.suggestions?.join(', ')}</span>
          </div>
        ))}
      </div>
    ),
  },
  spelling_suggestions: {
    dataKey: 'spelling_items',
    render: (items: Array<{ word: string; suggestions: string[] }>) => (
      <div className="py-2 px-2 space-y-1">
        {items.map((item, i) => (
          <div key={i} className="flex gap-3 text-xs">
            <span className="font-mono text-blue-400">{item.word}</span>
            <span className="text-muted-foreground">&rarr;</span>
            <span className="text-muted-foreground">{item.suggestions?.join(', ')}</span>
          </div>
        ))}
      </div>
    ),
  },
}

function ImagePreview({ src }: { src: string }) {
  const filename = (() => {
    try { return new URL(src, 'https://x.com').pathname.split('/').pop() || src } catch { return src }
  })()
  return (
    <div className="group relative">
      <div className="border rounded p-1 bg-muted/50 max-w-[120px]">
        <img src={src} alt="" className="max-w-full max-h-[60px] object-contain" loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
        <div className="text-[10px] font-mono text-muted-foreground truncate mt-0.5" title={src}>
          {filename}
        </div>
      </div>
      <div className="hidden group-hover:block absolute z-50 bottom-full left-0 mb-2 p-2 bg-popover border rounded-lg shadow-xl">
        <img src={src} alt="" className="max-w-[300px] max-h-[200px] object-contain" loading="lazy" />
        <div className="text-[10px] font-mono text-muted-foreground mt-1 max-w-[300px] break-all">{src}</div>
      </div>
    </div>
  )
}

function CopyAffectedUrlsButton({ pages }: { pages: Array<Record<string, unknown>> | unknown[] }) {
  const [copied, setCopied] = useState(false)
  // Prefer 'url' key, fall back to 'source_url' (for link-based issues), then 'target_url'
  const urls = (pages as Array<Record<string, unknown>>)
    .map((p) => {
      const candidate = p['url'] ?? p['source_url'] ?? p['target_url']
      return typeof candidate === 'string' && /^https?:\/\//.test(candidate) ? candidate : null
    })
    .filter((u): u is string => !!u)
  if (urls.length === 0) return null
  const label = `Copy URLs (${urls.length})`
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(urls.join('\n'))
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        } catch {
          /* clipboard blocked — silently ignore */
        }
      }}
      title={copied ? 'Copied' : `Copy all ${urls.length} affected URLs`}
    >
      {copied ? 'Copied' : label}
    </Button>
  )
}

function IssueDetailTable({ issueType, pages }: { issueType: string; pages: any[] }) {
  const columns = ISSUE_COLUMNS[issueType] || [{ key: 'url', label: 'URL', mono: true, truncate: true }]
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const expandConfig = EXPANDABLE_ISSUE_TYPES[issueType]

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  return (
    <div className="max-h-[500px] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {expandConfig && <TableHead className="text-xs px-1 w-6" />}
            {columns.map((col) => (
              <TableHead key={col.key} className="text-xs px-2">
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pages.map((page: Record<string, unknown>, i: number) => {
            const hasSubItems = expandConfig && Array.isArray(page[expandConfig.dataKey]) && (page[expandConfig.dataKey] as any[]).length > 0
            const isExpanded = expandedRows.has(i)
            return (
              <>
                <TableRow key={i} className={hasSubItems ? 'cursor-pointer' : ''} onClick={hasSubItems ? () => toggleRow(i) : undefined}>
                  {expandConfig && (
                    <TableCell className="px-1 py-1.5 w-6">
                      {hasSubItems && (isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />)}
                    </TableCell>
                  )}
                  {columns.map((col) => {
                    const value = page[col.key]
                    const raw = value === null || value === undefined ? '-' : String(value)
                    const display = raw.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'")
                    const isUrlColumn = (col.key === 'url' || col.key === 'source_url' || col.key === 'target_url' || col.key === 'redirect_url') && /^https?:\/\//.test(raw)
                    return (
                      <TableCell
                        key={col.key}
                        className={`text-xs py-1.5 px-2 ${col.mono ? 'font-mono' : ''} ${col.truncate ? 'max-w-[250px] truncate' : ''}`}
                        title={col.truncate ? display : undefined}
                      >
                        {col.key === 'status_code' ? (
                          <Badge
                            variant={
                              Number(value) >= 400 ? 'destructive' : Number(value) >= 300 ? 'secondary' : 'outline'
                            }
                          >
                            {display}
                          </Badge>
                        ) : isUrlColumn ? (
                          <a
                            href={raw}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary hover:underline"
                          >
                            {display}
                          </a>
                        ) : (
                          display
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
                {isExpanded && hasSubItems && (
                  <tr key={`${i}-sub`}>
                    <td colSpan={columns.length + 1} className="bg-muted/30 border-b">
                      {expandConfig.render(page[expandConfig.dataKey])}
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

type SortMode = 'priority' | 'severity' | 'count'

/** Sort issues by category order within each severity group */
function sortByCategory(items: CrawlIssue[]): CrawlIssue[] {
  return [...items].sort((a, b) => {
    const orderA = ISSUE_CATEGORIES[a.type]?.order ?? 99
    const orderB = ISSUE_CATEGORIES[b.type]?.order ?? 99
    return orderA - orderB
  })
}

function IssuesTab({ crawlId, totalPages, isWordPress }: { crawlId: string; totalPages: number; isWordPress?: boolean }) {
  const { data, isLoading } = useCrawlIssues(crawlId)
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null)
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [sortMode, setSortMode] = useState<SortMode>('priority')
  const { data: issuePages } = useIssuePages(crawlId, selectedIssue)

  const issues = data?.issues || []
  const errors = issues.filter((i) => i.severity === 'error')
  const warnings = issues.filter((i) => i.severity === 'warning')
  const infos = issues.filter((i) => i.severity === 'info')

  const errorCount = errors.reduce((sum, i) => sum + i.count, 0)
  const warningCount = warnings.reduce((sum, i) => sum + i.count, 0)
  const infoCount = infos.reduce((sum, i) => sum + i.count, 0)

  // Apply severity + category filters
  let filteredIssues = severityFilter
    ? issues.filter((i) => i.severity === severityFilter)
    : issues
  if (categoryFilter) {
    filteredIssues = filteredIssues.filter((i) => getIssueCategory(i.type).label === categoryFilter)
  }

  // Sort based on selected mode
  const sortIssues = (items: CrawlIssue[]): CrawlIssue[] => {
    if (sortMode === 'count') {
      return [...items].sort((a, b) => b.count - a.count)
    }
    if (sortMode === 'priority') {
      return [...items].sort((a, b) => {
        const pa = computePriority(a.type, a.severity, a.count, totalPages)
        const pb = computePriority(b.type, b.severity, b.count, totalPages)
        return pb - pa
      })
    }
    // severity mode: group by severity, sort by category within
    return sortByCategory(items)
  }

  // In priority/count mode, render a flat sorted list; in severity mode, group by severity
  const useFlatList = sortMode === 'priority' || sortMode === 'count'
  const sortedFlat = useFlatList ? sortIssues(filteredIssues) : []
  const filteredErrors = !useFlatList ? sortByCategory(filteredIssues.filter((i) => i.severity === 'error')) : []
  const filteredWarnings = !useFlatList ? sortByCategory(filteredIssues.filter((i) => i.severity === 'warning')) : []
  const filteredInfos = !useFlatList ? sortByCategory(filteredIssues.filter((i) => i.severity === 'info')) : []

  // Compute which categories are present in the current issues (includes AI categories)
  const activeCategories = new Set(issues.map((i) => getIssueCategory(i.type).label))
  const allCategories = [...getCategoryLabels(), ...Array.from(activeCategories)].filter(
    (c, idx, arr) => activeCategories.has(c) && arr.indexOf(c) === idx
  )

  if (isLoading) return <Skeleton className="h-48 w-full" />

  if (issues.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No issues found. The site looks healthy.
        </CardContent>
      </Card>
    )
  }

  // Crawl-level issues where count doesn't represent affected pages
  const CRAWL_LEVEL_ISSUES = new Set([
    'missing_sitemap', 'ai_fully_blocked', 'ai_partially_blocked',
    'ai_blocked_no_llms_txt', 'ai_contradictory_signals', 'ai_missing_llms_txt',
    'ai_not_ready',
  ])

  // Link-based issues where count represents link instances, not affected pages
  const LINK_BASED_ISSUES = new Set([
    'broken_internal_links',
    'nofollow_internal_links',
    'empty_anchor_text',
    'broken_external_links',
    'links_to_redirects',
    'spammy_outbound_links',
  ])

  const affectedPct = (issue: CrawlIssue) => {
    if (totalPages <= 0 || CRAWL_LEVEL_ISSUES.has(issue.type)) return ''
    if (LINK_BASED_ISSUES.has(issue.type)) return ''
    const pct = Math.round((issue.count / totalPages) * 100)
    if (pct > 100) return ''
    return pct > 0 ? `${pct}%` : '<1%'
  }

  const renderIssueButton = (issue: CrawlIssue) => {
    const cat = getIssueCategory(issue.type)
    const isAi = !ISSUE_CATEGORIES[issue.type]
    return (
      <button
        key={issue.type}
        onClick={() => setSelectedIssue(selectedIssue === issue.type ? null : issue.type)}
        className={`w-full flex items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 ${
          selectedIssue === issue.type ? 'border-primary bg-accent/50' : ''
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isAi ? <Sparkles className="h-4 w-4 shrink-0 text-purple-500" /> : severityIcon(issue.severity)}
          <span className="text-sm truncate">{issue.label}</span>
          {!categoryFilter && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{cat.label}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <span className="text-[10px] text-muted-foreground">{affectedPct(issue)}</span>
          <Badge variant="secondary">
            {LINK_BASED_ISSUES.has(issue.type)
              ? `${issue.count} links`
              : issue.count}
          </Badge>
        </div>
      </button>
    )
  }

  const renderGroup = (label: string, items: CrawlIssue[]) => {
    if (items.length === 0) return null
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{label}</h3>
        {items.map(renderIssueButton)}
      </div>
    )
  }

  const sortLabels: Record<SortMode, string> = { priority: 'Priority', severity: 'Severity', count: 'Count' }
  const nextSort: Record<SortMode, SortMode> = { priority: 'severity', severity: 'count', count: 'priority' }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setSeverityFilter(severityFilter === 'error' ? null : 'error')}
          aria-label={`${errorCount} error ${errorCount === 1 ? 'issue' : 'issues'} across ${errors.length} ${errors.length === 1 ? 'type' : 'types'}`}
          className={`rounded-lg border p-3 text-center transition-colors hover:bg-accent/50 ${severityFilter === 'error' ? 'border-destructive bg-destructive/5' : ''}`}
        >
          <AlertCircle className="mx-auto h-5 w-5 text-destructive" />
          <p className="mt-1 text-2xl font-bold">{errorCount}</p>
          <p className="text-xs text-muted-foreground">{errors.length} {errors.length === 1 ? 'error type' : 'error types'}</p>
        </button>
        <button
          onClick={() => setSeverityFilter(severityFilter === 'warning' ? null : 'warning')}
          aria-label={`${warningCount} warning ${warningCount === 1 ? 'issue' : 'issues'} across ${warnings.length} ${warnings.length === 1 ? 'type' : 'types'}`}
          className={`rounded-lg border p-3 text-center transition-colors hover:bg-accent/50 ${severityFilter === 'warning' ? 'border-yellow-500 bg-yellow-500/5' : ''}`}
        >
          <AlertTriangle className="mx-auto h-5 w-5 text-yellow-500" />
          <p className="mt-1 text-2xl font-bold">{warningCount}</p>
          <p className="text-xs text-muted-foreground">{warnings.length} {warnings.length === 1 ? 'warning type' : 'warning types'}</p>
        </button>
        <button
          onClick={() => setSeverityFilter(severityFilter === 'info' ? null : 'info')}
          aria-label={`${infoCount} info ${infoCount === 1 ? 'issue' : 'issues'} across ${infos.length} ${infos.length === 1 ? 'type' : 'types'}`}
          className={`rounded-lg border p-3 text-center transition-colors hover:bg-accent/50 ${severityFilter === 'info' ? 'border-blue-500 bg-blue-500/5' : ''}`}
        >
          <Info className="mx-auto h-5 w-5 text-blue-500" />
          <p className="mt-1 text-2xl font-bold">{infoCount}</p>
          <p className="text-xs text-muted-foreground">{infos.length} {infos.length === 1 ? 'info type' : 'info types'}</p>
        </button>
      </div>

      {/* Sort toggle + category filter pills */}
      <div className="flex items-center justify-between gap-2">
        {allCategories.length > 1 ? (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategoryFilter(null)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                !categoryFilter ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'
              }`}
            >
              All
            </button>
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  categoryFilter === cat ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        ) : <div />}
        <button
          onClick={() => setSortMode(nextSort[sortMode])}
          className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
          title={`Sort by ${sortLabels[nextSort[sortMode]]}`}
        >
          <ArrowUpDown className="h-3 w-3" />
          {sortLabels[sortMode]}
        </button>
      </div>

      {/* Issue list + detail panel */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          {useFlatList ? (() => {
            // Segment: "site-level" = affects 100% of pages (applies uniformly, fix-once),
            // "per-page" = variable coverage (triage by count/percentage).
            const siteLevel = sortedFlat.filter((i) => affectedPct(i) === '100%')
            const perPage = sortedFlat.filter((i) => affectedPct(i) !== '100%')
            return (
              <>
                {perPage.length > 0 && (
                  <div className="space-y-2">
                    {siteLevel.length > 0 && (
                      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Per-page issues</h3>
                    )}
                    {perPage.map(renderIssueButton)}
                  </div>
                )}
                {siteLevel.length > 0 && (
                  <details className="space-y-2 group" open={perPage.length === 0}>
                    <summary className="cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors list-none flex items-center gap-1.5">
                      <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                      Site-wide configuration ({siteLevel.length})
                      <span className="text-[10px] normal-case tracking-normal font-normal ml-1">— fix once, affects whole site</span>
                    </summary>
                    <div className="space-y-2 pt-1">
                      {siteLevel.map(renderIssueButton)}
                    </div>
                  </details>
                )}
              </>
            )
          })() : (
            <>
              {renderGroup('Errors', filteredErrors)}
              {renderGroup('Warnings', filteredWarnings)}
              {renderGroup('Info', filteredInfos)}
            </>
          )}
        </div>

        <div className="space-y-4">
          {selectedIssue && issueDescriptions[selectedIssue] && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Issue Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground mb-1">Description</p>
                  <p>{issueDescriptions[selectedIssue].description}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground mb-1">How To Fix</p>
                  <div className="prose prose-sm dark:prose-invert max-w-none text-foreground prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-headings:mt-3 prose-headings:mb-1 prose-a:text-primary">
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {issueDescriptions[selectedIssue].howToFix}
                    </Markdown>
                    {isWordPress && issueDescriptions[selectedIssue].wordpressFix && (
                      <div className="mt-3 p-3 rounded-md bg-amber-500/10 dark:bg-amber-500/10 border border-amber-500/20 [&_a]:text-amber-700 dark:[&_a]:text-amber-300 [&_a]:underline [&_code]:text-amber-800 dark:[&_code]:text-amber-200 [&_code]:bg-amber-500/15 [&_strong]:text-amber-800 dark:[&_strong]:text-amber-200">
                        <p className="font-medium text-amber-700 dark:text-amber-400 mb-1 text-xs uppercase tracking-wide">WordPress</p>
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {issueDescriptions[selectedIssue].wordpressFix!}
                        </Markdown>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* AI issue detail view — per-page descriptions + recommendations */}
          {selectedIssue && !issueDescriptions[selectedIssue] && issuePages?.isAiIssue && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  AI-Detected Issue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(issuePages.pages as AiIssuePage[]).slice(0, 20).map((ap) => (
                    <div key={ap.url} className="rounded border p-3 space-y-1">
                      <a
                        href={ap.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-xs break-all"
                      >
                        {ap.title || ap.url}
                      </a>
                      {ap.description && (
                        <p className="text-muted-foreground">{ap.description}</p>
                      )}
                      {ap.recommendation && (
                        <p className="text-foreground/70"><span className="font-medium">Fix:</span> {ap.recommendation}</p>
                      )}
                    </div>
                ))}
              </CardContent>
            </Card>
          )}
          {selectedIssue && issuePages?.pages && !issuePages.isAiIssue && (
            <Card>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">
                  Affected Pages ({issuePages.totalCount != null && issuePages.totalCount > issuePages.pages.length
                    ? `${issuePages.pages.length.toLocaleString()} of ${issuePages.totalCount.toLocaleString()}`
                    : issuePages.pages.length.toLocaleString()})
                </CardTitle>
                <CopyAffectedUrlsButton pages={issuePages.pages as unknown as Array<Record<string, unknown>>} />
              </CardHeader>
              <CardContent>
                <IssueDetailTable issueType={selectedIssue} pages={issuePages.pages} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

function PagesTab({ crawlId }: { crawlId: string }) {
  const [search, setSearch] = useState('')
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const debouncedSearch = useDebounce(search, 300)

  // Reset to page 0 when search changes
  useEffect(() => {
    setPageIndex(0)
  }, [debouncedSearch])

  const { data, isLoading } = useCrawlPages(crawlId, {
    search: debouncedSearch,
    limit: pageSize,
    offset: pageIndex * pageSize,
  })
  const pages = data?.pages || []
  const total = data?.total || 0
  const pageCount = Math.ceil(total / pageSize)
  const startRow = total === 0 ? 0 : pageIndex * pageSize + 1
  const endRow = Math.min((pageIndex + 1) * pageSize, total)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search pages by URL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            setPageSize(Number(v))
            setPageIndex(0)
          }}
        >
          <SelectTrigger className="h-10 w-[110px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[25, 50, 100, 200].map((size) => (
              <SelectItem key={size} value={String(size)} className="text-xs">
                {size} rows
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : pages.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No pages found.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[300px]">URL</TableHead>
                <TableHead className="w-[60px]">Status</TableHead>
                <TableHead className="w-[80px]">Time (ms)</TableHead>
                <TableHead className="min-w-[200px]">Title</TableHead>
                <TableHead className="w-[60px]">Words</TableHead>
                <TableHead className="w-[40px]">H1s</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow
                  key={page.id}
                  className="cursor-pointer hover:bg-accent/50"
                  onClick={() => setSelectedPageId(page.id)}
                >
                  <TableCell className="font-mono text-xs max-w-[300px]">
                    <span className="truncate block" title={page.url}>
                      {page.url}
                      {page.js_rendered === 1 && (
                        <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 inline align-middle" title={`Re-rendered via browser — original word count: ${page.pre_render_word_count ?? 0}`}>
                          JS
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        page.status_code >= 400 ? 'destructive' :
                        page.status_code >= 300 ? 'secondary' : 'outline'
                      }
                    >
                      {page.status_code}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{page.response_time_ms}</TableCell>
                  <TableCell className="text-xs truncate max-w-[200px]" title={page.title ? decodeHtmlEntities(page.title) : ''}>
                    {page.title ? decodeHtmlEntities(page.title) : <span className="text-muted-foreground">Missing</span>}
                  </TableCell>
                  <TableCell className="text-xs">{page.word_count}</TableCell>
                  <TableCell className="text-xs">
                    {page.h1_count === 0 ? (
                      <span className="text-destructive">0</span>
                    ) : page.h1_count > 1 ? (
                      <span className="text-yellow-500">{page.h1_count}</span>
                    ) : (
                      page.h1_count
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {startRow}–{endRow} of {total} pages
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => setPageIndex(pageIndex - 1)}
              disabled={pageIndex === 0}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="px-2">
              Page {pageIndex + 1} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => setPageIndex(pageIndex + 1)}
              disabled={pageIndex >= pageCount - 1}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <PageDetailSheet
        crawlId={crawlId}
        pageId={selectedPageId}
        onClose={() => setSelectedPageId(null)}
      />
    </div>
  )
}

function WordPressTab({ crawlId }: { crawlId: string }) {
  const { data, isLoading } = useWordPressInfo(crawlId, true)

  if (isLoading) return <Skeleton className="h-48 w-full" />

  if (!data?.isWordpress) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          WordPress not detected for this site.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* WP overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">WordPress Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
            <div>
              <p className="text-muted-foreground">Version</p>
              <p className="text-lg font-semibold">{data.version || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">SEO Plugin</p>
              <p className="text-lg font-semibold capitalize">{data.seoPlugin || 'None detected'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">WP Issues</p>
              <p className="text-lg font-semibold">{data.issues.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WP-specific issues */}
      {data.issues.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">WordPress-Specific Issues</CardTitle>
            <CardDescription>
              Issues detected via WordPress REST API and URL patterns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.issues.map((issue) => (
                <div
                  key={issue.type}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    {severityIcon(issue.severity)}
                    <span className="text-sm">{issue.label}</span>
                  </div>
                  <Badge variant="secondary">{issue.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No WordPress-specific issues found.
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function LighthouseButton({ crawlId }: { crawlId: string }) {
  const runLighthouse = useRunLighthouse(crawlId)
  const { data: lhData } = useLighthouseData(crawlId)
  const collected = lhData?.collected ?? 0

  const handleRun = (limit: number) => {
    runLighthouse.mutate(limit, {
      onSuccess: (data) => {
        toast.success(`Lighthouse started for ${data.target} pages (~$${data.estimatedCost})`)
      },
      onError: () => toast.error('Failed to start Lighthouse'),
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={runLighthouse.isPending}>
          {runLighthouse.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Zap className="mr-2 h-4 w-4" />
          )}
          Lighthouse{collected > 0 ? ` (${collected})` : ''}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleRun(10)}>
          Top 10 pages (~$0.04)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleRun(20)}>
          Top 20 pages (~$0.09)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleRun(50)}>
          Top 50 pages (~$0.21)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleRun(100)}>
          Top 100 pages (~$0.43)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ExportButton({ crawlId }: { crawlId: string }) {
  const exportCrawl = useExportCrawl()

  const handleExport = (type: ExportType, format: ExportFormat) => {
    exportCrawl.mutate({ id: crawlId, type, format })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={exportCrawl.isPending}>
          {exportCrawl.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('pages', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Pages (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('issues', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Issues (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('links', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Links (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('images', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Images (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('headings', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Headings (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('schema', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Schema Data (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('spelling', 'csv')}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Spelling Issues (CSV)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport('pages', 'json')}>
          <FileJson className="mr-2 h-4 w-4" />
          Full Crawl (JSON)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport('sitemap-xml', 'json')}>
          <Map className="mr-2 h-4 w-4" />
          Sitemap (XML)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => window.open(`/api/crawls/${crawlId}/report`, '_blank')}>
          <FileText className="mr-2 h-4 w-4" />
          View HTML Report
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open(`/api/crawls/${crawlId}/report/pdf`, '_blank')}>
          <FileText className="mr-2 h-4 w-4" />
          Download PDF Report
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function CrawlDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'pages'
  const handleTabChange = (value: string) => {
    setSearchParams(value === 'overview' ? {} : { tab: value }, { replace: false })
  }
  const { data, isLoading } = useCrawl(id!)
  const cancelCrawl = useCancelCrawl()
  const pauseCrawl = usePauseCrawl()
  const resumeCrawl = useResumeCrawl()

  const crawl = data?.crawl

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!crawl) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Crawl not found</p>
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mt-4">
          Back to Crawls
        </Button>
      </div>
    )
  }

  let hasExtractionRules = false
  let checkExternalLinks = false
  try {
    const cfg = JSON.parse(crawl.config)
    hasExtractionRules = Array.isArray(cfg.extractionRules) && cfg.extractionRules.length > 0
    checkExternalLinks = cfg.checkExternalLinks === true
  } catch { /* ignore */ }

  const handleCancel = async () => {
    await cancelCrawl.mutateAsync(id!)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <Link to={crawl.site_id ? `/dashboard/sites/${crawl.site_id}` : '/dashboard/sites'} className="shrink-0">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0 basis-0 sm:basis-auto">
          <h1 className="text-2xl font-bold tracking-tight">Crawl Report</h1>
          <p className="text-sm text-muted-foreground truncate">
            <Link to="/dashboard/sites" className="hover:underline">Sites</Link>
            {' / '}
            {crawl.site_id ? (
              <Link to={`/dashboard/sites/${crawl.site_id}`} className="hover:underline">{crawl.domain}</Link>
            ) : (
              <span>{crawl.domain}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
        {(crawl.status === 'completed' || crawl.status === 'failed' || crawl.status === 'cancelled') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const params = new URLSearchParams({ url: crawl.url })
              try {
                const cfg = JSON.parse(crawl.config)
                if (cfg.maxPages) params.set('maxPages', String(cfg.maxPages))
                if (cfg.rateLimit) params.set('rateLimit', String(cfg.rateLimit))
                if (cfg.respectRobots !== undefined) params.set('respectRobots', String(cfg.respectRobots))
                if (cfg.mode) params.set('mode', cfg.mode)
              } catch { /* use defaults */ }
              navigate(`/dashboard/crawls/new?${params.toString()}`)
            }}
          >
            <RotateCw className="mr-2 h-4 w-4" />
            Recrawl
          </Button>
        )}
        {(crawl.status === 'completed' || crawl.pages_crawled > 0) && (
          <>
            <ShareReportDialog crawlId={id!} domain={crawl.domain} />
            {crawl.status === 'completed' && <LighthouseButton crawlId={id!} />}
            <ExportButton crawlId={id!} />
          </>
        )}
        {crawl.status === 'pending' && (
          <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelCrawl.isPending}>
            <XCircle className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        )}
        {crawl.status === 'running' && (
          <>
            <Button variant="secondary" size="sm" onClick={() => pauseCrawl.mutate(id!)} disabled={pauseCrawl.isPending}>
              <Pause className="mr-2 h-4 w-4" />
              Pause
            </Button>
            <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelCrawl.isPending}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </>
        )}
        {crawl.status === 'paused' && (
          <>
            <Button variant="default" size="sm" onClick={() => resumeCrawl.mutate(id!)} disabled={resumeCrawl.isPending}>
              <Play className="mr-2 h-4 w-4" />
              Resume
            </Button>
            <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelCrawl.isPending}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </>
        )}
        </div>
      </div>

      <ProgressCard crawl={crawl} />

      {(crawl.status === 'running' || crawl.status === 'pending') && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Crawl in progress — data will update as pages are processed.
          </AlertDescription>
        </Alert>
      )}
      {crawl.status === 'paused' && (
        <Alert>
          <Pause className="h-4 w-4" />
          <AlertDescription>
            Crawl is paused. Click Resume to continue crawling.
          </AlertDescription>
        </Alert>
      )}

      {(crawl.status === 'completed' || crawl.pages_crawled > 0) && (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-transparent gap-1.5 p-0 h-auto flex flex-wrap">
            {/* Primary tabs — Lanz's core workflow */}
            <TabsTrigger value="pages" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <FileText className="mr-1.5 h-3.5 w-3.5" />
              Pages
            </TabsTrigger>
            <TabsTrigger value="response-codes" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <Shield className="mr-1.5 h-3.5 w-3.5" />
              Response Codes
            </TabsTrigger>
            <TabsTrigger value="content" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <FileWarning className="mr-1.5 h-3.5 w-3.5" />
              Content
            </TabsTrigger>
            <TabsTrigger value="images" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <ImageIcon className="mr-1.5 h-3.5 w-3.5" />
              Images
            </TabsTrigger>
            <TabsTrigger value="directives" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <Shield className="mr-1.5 h-3.5 w-3.5" />
              Directives
            </TabsTrigger>
            <TabsTrigger value="links" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <Link2 className="mr-1.5 h-3.5 w-3.5" />
              Links
            </TabsTrigger>
            <TabsTrigger value="external-links" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <Link2 className="mr-1.5 h-3.5 w-3.5" />
              External Links
            </TabsTrigger>
            <TabsTrigger value="sitemap" className="shrink-0 border border-border data-[state=active]:border-primary data-[state=active]:bg-primary/10 dark:data-[state=active]:bg-primary/15 rounded-md px-3 py-1.5">
              <Map className="mr-1.5 h-3.5 w-3.5" />
              Sitemap
            </TabsTrigger>
            {/* Advanced tabs — behind "More" dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`shrink-0 border rounded-md px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                    ['overview', 'issues', 'data', 'security', 'compare', 'structure', 'content-intel', 'extractions', 'wordpress'].includes(activeTab)
                      ? 'border-primary bg-primary/10 dark:bg-primary/15 text-primary'
                      : 'border-border hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                  More
                  {['overview', 'issues', 'data', 'security', 'compare', 'structure', 'content-intel', 'extractions', 'wordpress'].includes(activeTab) && (
                    <span className="text-xs opacity-70">({activeTab.replace('-', ' ')})</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleTabChange('overview')} className="gap-2">
                  <BarChart3 className="h-3.5 w-3.5" /> Overview
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('issues')} className="gap-2">
                  <AlertTriangle className="h-3.5 w-3.5" /> Issues
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('data')} className="gap-2">
                  <TableProperties className="h-3.5 w-3.5" /> Data
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleTabChange('security')} className="gap-2">
                  <Lock className="h-3.5 w-3.5" /> Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('compare')} className="gap-2">
                  <GitCompare className="h-3.5 w-3.5" /> Compare
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('structure')} className="gap-2">
                  <FolderTree className="h-3.5 w-3.5" /> Structure
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('content-intel')} className="gap-2">
                  <BookOpen className="h-3.5 w-3.5" /> Content Intel
                </DropdownMenuItem>
                {hasExtractionRules && (
                  <DropdownMenuItem onClick={() => handleTabChange('extractions')} className="gap-2">
                    <Pickaxe className="h-3.5 w-3.5" /> Extractions
                  </DropdownMenuItem>
                )}
                {!!crawl.is_wordpress && (
                  <DropdownMenuItem onClick={() => handleTabChange('wordpress')} className="gap-2">
                    <Globe className="h-3.5 w-3.5" /> WordPress
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <OverviewTab crawlId={id!} aiCrawlerRules={crawl.ai_crawler_rules} hasLlmsTxt={crawl.has_llms_txt} hasAiAnalysis={crawl.has_ai_analysis} />
          </TabsContent>
          <TabsContent value="issues" className="mt-4">
            <IssuesTab crawlId={id!} totalPages={crawl.pages_crawled} isWordPress={!!crawl.is_wordpress} />
          </TabsContent>
          <TabsContent value="pages" className="mt-4">
            <PagesTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="data" className="mt-4">
            <DataTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="response-codes" className="mt-4">
            <FilteredPagesTab
              crawlId={id!}
              defaultFilter="status_2xx"
              filterOptions={[
                { label: '2xx Success', value: 'status_2xx' },
                { label: '3xx Redirect', value: 'status_3xx' },
                { label: '4xx Client Error', value: 'status_4xx' },
                { label: '5xx Server Error', value: 'status_5xx' },
              ]}
              defaultSortBy="status_code"
            />
          </TabsContent>
          <TabsContent value="content" className="mt-4">
            <FilteredPagesTab
              crawlId={id!}
              defaultFilter="content_thin"
              filterOptions={[
                { label: 'Empty (0 words)', value: 'content_empty' },
                { label: 'Thin (<200)', value: 'content_thin' },
                { label: 'Short (200-499)', value: 'content_short' },
                { label: 'Medium (500-999)', value: 'content_medium' },
                { label: 'Long (1000+)', value: 'content_long' },
                { label: 'Missing H1', value: 'missing_h1' },
                { label: 'Multiple H1s', value: 'multiple_h1' },
                { label: 'Bad Heading Order', value: 'non_sequential_headings' },
                { label: 'Easy Reading (60+)', value: 'readability_easy' },
                { label: 'Moderate (30-59)', value: 'readability_moderate' },
                { label: 'Difficult (<30)', value: 'readability_difficult' },
                { label: 'Unscored', value: 'readability_unscored' },
              ]}
              defaultSortBy="word_count"
              defaultSortDesc
              columnVisibility={{
                status_code: true,
                title: true,
                word_count: true,
                url: true,
                response_time_ms: false,
                meta_description: false,
                h1_count: false,
                canonical_status: false,
                images_count: false,
                images_missing_alt: false,
                internal_links_count: false,
                external_links_count: false,
                has_schema: false,
                has_og_tags: false,
              }}
            />
          </TabsContent>
          <TabsContent value="images" className="mt-4">
            <ImagesTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="directives" className="mt-4">
            <FilteredPagesTab
              crawlId={id!}
              defaultFilter="noindex"
              filterOptions={[
                { label: 'Noindex', value: 'noindex' },
                { label: 'Nofollow', value: 'nofollow' },
                { label: 'Indexable', value: 'indexable' },
                { label: 'Not Indexable', value: 'not_indexable' },
              ]}
              defaultSortBy="url"
              columnVisibility={{
                status_code: true,
                title: true,
                url: true,
                meta_description: false,
                word_count: false,
                response_time_ms: false,
                h1_count: false,
                canonical_status: true,
                images_count: false,
                images_missing_alt: false,
                internal_links_count: false,
                external_links_count: false,
                has_schema: false,
                has_og_tags: false,
              }}
            />
          </TabsContent>
          <TabsContent value="security" className="mt-4">
            <SecurityTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="links" className="mt-4">
            <LinksTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="external-links" className="mt-4">
            <ExternalLinksTab crawlId={id!} checkExternalLinks={checkExternalLinks} />
          </TabsContent>
          <TabsContent value="compare" className="mt-4">
            <CompareTab crawlId={id!} initialOtherId={searchParams.get('other') ?? undefined} />
          </TabsContent>
          <TabsContent value="sitemap" className="mt-4">
            <SitemapTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="structure" className="mt-4">
            <StructureTab crawlId={id!} />
          </TabsContent>
          <TabsContent value="content-intel" className="mt-4">
            <ContentIntelligenceTab crawlId={id!} />
          </TabsContent>
          {hasExtractionRules && (
            <TabsContent value="extractions" className="mt-4">
              <ExtractionsTab crawlId={id!} />
            </TabsContent>
          )}
          {!!crawl.is_wordpress && (
            <TabsContent value="wordpress" className="mt-4">
              <WordPressTab crawlId={id!} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  )
}
